import { PROVIDERS } from '@/providers';
import {
  checkApiKeyExists,
  saveApiKeyToEnv,
  removeApiKey,
  maskApiKey,
  SEARCH_PROVIDERS,
  DRISHTI_API_KEY_NAME,
  type SearchProviderId,
} from '../utils/env.js';
import {
  validateDrishtiApiKey,
  type DrishtiApiKeyValidationResult,
} from '../tools/finance/drishti-account.js';

const SELECTION_STATES = ['service_select', 'action_select', 'key_input'] as const;

export type KeySelectionAppState = 'idle' | (typeof SELECTION_STATES)[number];

/** A single credential the user can manage from the `/keys` screen. */
export interface KeyService {
  /** Stable id for selection. */
  id: string;
  /** Human-readable label (e.g. "Anthropic", "Drishti", "Exa (search)"). */
  displayName: string;
  /** Environment variable / store key name. */
  envVar: string;
  /** Grouping for display. */
  group: 'llm' | 'data' | 'search';
}

export interface KeySelectionState {
  appState: KeySelectionAppState;
  services: KeyService[];
  pendingService: KeyService | null;
}

type ChangeListener = () => void;
type DrishtiKeyValidator = (apiKey: string) => Promise<DrishtiApiKeyValidationResult>;

/**
 * Build the list of manageable credentials: LLM providers that require a key,
 * the Drishti data key, and the web-search providers.
 */
function buildServices(): KeyService[] {
  const llm: KeyService[] = PROVIDERS.filter((p) => p.apiKeyEnvVar).map((p) => ({
    id: `llm:${p.id}`,
    displayName: p.displayName,
    envVar: p.apiKeyEnvVar!,
    group: 'llm',
  }));

  const data: KeyService[] = [
    { id: 'data:drishti', displayName: 'Drishti (India data)', envVar: DRISHTI_API_KEY_NAME, group: 'data' },
  ];

  const search: KeyService[] = (Object.keys(SEARCH_PROVIDERS) as SearchProviderId[]).map((id) => ({
    id: `search:${id}`,
    displayName: `${SEARCH_PROVIDERS[id].displayName} (search)`,
    envVar: SEARCH_PROVIDERS[id].apiKeyEnvVar,
    group: 'search',
  }));

  return [...llm, ...data, ...search];
}

/**
 * Controller for the `/keys` credential manager. Drives a small state machine —
 * pick a service, choose set/remove, then enter a masked key — reusing the
 * existing ApiKeyInput / select-list components rendered by the CLI.
 */
export class KeySelectionController {
  private appStateValue: KeySelectionAppState = 'idle';
  private servicesValue: KeyService[] = [];
  private pendingServiceValue: KeyService | null = null;
  private readonly onError: (message: string) => void;
  private readonly onInfo: (message: string) => void;
  private readonly onChange?: ChangeListener;
  private readonly validateDrishtiKey: DrishtiKeyValidator;

  constructor(
    onError: (message: string) => void,
    onInfo: (message: string) => void,
    onChange?: ChangeListener,
    validateDrishtiKey: DrishtiKeyValidator = validateDrishtiApiKey,
  ) {
    this.onError = onError;
    this.onInfo = onInfo;
    this.onChange = onChange;
    this.validateDrishtiKey = validateDrishtiKey;
  }

  get state(): KeySelectionState {
    return {
      appState: this.appStateValue,
      services: this.servicesValue,
      pendingService: this.pendingServiceValue,
    };
  }

  isInSelectionFlow(): boolean {
    return this.appStateValue !== 'idle';
  }

  /** Label for a service showing whether a key is configured (masked). */
  serviceLabel(service: KeyService): string {
    const value = process.env[service.envVar];
    const status = checkApiKeyExists(service.envVar)
      ? `set ${maskApiKey(value ?? '')}`
      : 'not set';
    return `${service.displayName} — ${status}`;
  }

  startSelection() {
    this.servicesValue = buildServices();
    this.pendingServiceValue = null;
    this.appStateValue = 'service_select';
    this.emitChange();
  }

  cancelSelection() {
    this.resetPendingState();
  }

  handleServiceSelect(serviceId: string | null) {
    if (!serviceId) {
      this.resetPendingState();
      return;
    }
    const service = this.servicesValue.find((s) => s.id === serviceId);
    if (!service) {
      this.resetPendingState();
      return;
    }
    this.pendingServiceValue = service;
    this.appStateValue = 'action_select';
    this.emitChange();
  }

  /** action: 'set' to enter a key, 'remove' to delete it, null to go back. */
  handleActionSelect(action: 'set' | 'remove' | null) {
    if (!action || !this.pendingServiceValue) {
      // Back to the service list.
      this.pendingServiceValue = null;
      this.appStateValue = 'service_select';
      this.emitChange();
      return;
    }

    if (action === 'remove') {
      const service = this.pendingServiceValue;
      if (removeApiKey(service.envVar)) {
        this.onInfo(`Removed ${service.displayName} API key.`);
      } else {
        this.onError(`Failed to remove ${service.displayName} API key.`);
      }
      this.pendingServiceValue = null;
      this.appStateValue = 'service_select';
      this.emitChange();
      return;
    }

    this.appStateValue = 'key_input';
    this.emitChange();
  }

  async handleKeySubmit(apiKey: string | null) {
    const service = this.pendingServiceValue;
    if (!service) {
      this.resetPendingState();
      return;
    }

    if (!apiKey) {
      // Cancelled input — return to the service list without changes.
      this.pendingServiceValue = null;
      this.appStateValue = 'service_select';
      this.emitChange();
      return;
    }

    const value = apiKey.trim();
    if (service.envVar === DRISHTI_API_KEY_NAME) {
      const validation = await this.validateDrishtiKey(value);
      if (validation.status === 'invalid_key') {
        this.onError('That Drishti API key looks incorrect. Please paste a valid key.');
        this.appStateValue = 'key_input';
        this.emitChange();
        return;
      }
      if (validation.status === 'error') {
        this.onError('Could not verify the Drishti key right now. Please try again.');
        this.appStateValue = 'key_input';
        this.emitChange();
        return;
      }
    }

    if (saveApiKeyToEnv(service.envVar, value)) {
      this.onInfo(`Saved ${service.displayName} API key.`);
    } else {
      this.onError(`Failed to save ${service.displayName} API key.`);
    }
    this.pendingServiceValue = null;
    this.appStateValue = 'service_select';
    this.emitChange();
  }

  private resetPendingState() {
    this.pendingServiceValue = null;
    this.appStateValue = 'idle';
    this.emitChange();
  }

  private emitChange() {
    this.onChange?.();
  }
}
